import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Icon } from '@iconify/react';
import type { CashflowTransaction } from '@/types/cashflow';
import { formatDateIndo, formatRupiahPlain, SHORT_MONTH_NAMES_ID } from '@/utils/cashflowUtils';

interface CashflowTransactionListProps {
  transactions: CashflowTransaction[];
  totalCount: number;
  manualCount: number;
  currentFilter: string;
  onFilterChange: (filter: string) => void;
  selectedMonth: number;
  selectedYear: number;
  onPrevMonth: () => void;
  onNextMonth: () => void;
  isLoading?: boolean;
  page: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  onEditTransaction: (tx: CashflowTransaction) => void;
  onDeleteTransaction: (tx: CashflowTransaction) => void;
  onAddTransaction?: () => void;
}

export const CashflowTransactionList: React.FC<CashflowTransactionListProps> = ({
  transactions,
  totalCount,
  manualCount,
  currentFilter,
  onFilterChange,
  selectedMonth,
  selectedYear,
  onPrevMonth,
  onNextMonth,
  isLoading,
  page,
  totalPages,
  onPageChange,
  onEditTransaction,
  onDeleteTransaction,
  onAddTransaction,
}) => {
  const [activeMenu, setActiveMenu] = useState<{
    tx: CashflowTransaction;
    top: number;
    left: number;
  } | null>(null);

  const handleOpenMenu = (e: React.MouseEvent<HTMLButtonElement>, tx: CashflowTransaction) => {
    e.stopPropagation();
    if (activeMenu?.tx.id === tx.id) {
      setActiveMenu(null);
      return;
    }
    const rect = e.currentTarget.getBoundingClientRect();
    const menuWidth = 120;
    const menuHeight = 82;

    const opensUpward = rect.bottom + menuHeight > window.innerHeight;
    const top = opensUpward ? Math.max(8, rect.top - menuHeight - 4) : rect.bottom + 4;

    let left = rect.right - menuWidth;
    if (left < 8) left = 8;
    if (left + menuWidth > window.innerWidth - 8) {
      left = window.innerWidth - menuWidth - 8;
    }

    setActiveMenu({ tx, top, left });
  };

  useEffect(() => {
    if (!activeMenu) return;

    const handleClose = () => {
      setActiveMenu(null);
    };

    window.addEventListener('resize', handleClose);
    window.addEventListener('scroll', handleClose, true);
    return () => {
      window.removeEventListener('resize', handleClose);
      window.removeEventListener('scroll', handleClose, true);
    };
  }, [activeMenu]);

  // Status Pending is excluded from Catatan Kas table (only Completed transactions are shown)
  const completedTransactions = transactions.filter((tx) => tx.status !== 'pending');

  const shortMonth = SHORT_MONTH_NAMES_ID[selectedMonth - 1] || 'Sep';
  const monthLabel = `${shortMonth} ${selectedYear}`;

  const filters = [
    { key: 'all', label: 'Semua' },
    { key: 'income', label: 'Pemasukan' },
    { key: 'expense', label: 'Pengeluaran' },
    { key: 'manual', label: 'Manual' },
  ];

  return (
    <div className="bg-white rounded-2xl p-5 sm:p-6 border border-[#E0E6ED] shadow-2xs space-y-4">
      {/* 1. Header Row (Title & Subtitle + Desktop "+ Catat transaksi" Button) */}
      <div className="flex items-start sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-base sm:text-lg font-bold text-[#0E1726] leading-tight">
            Catatan kas
          </h2>
        </div>

        {onAddTransaction && (
          <button
            type="button"
            onClick={onAddTransaction}
            className="hidden sm:inline-flex items-center gap-1.5 px-3.5 py-2 rounded-xl bg-[#FC480D] hover:bg-[#e03d07] active:scale-[0.99] text-white text-xs font-semibold shadow-2xs transition-all cursor-pointer shrink-0"
          >
            <Icon icon="ph:plus-bold" className="w-3.5 h-3.5" />
            <span>Catat transaksi</span>
          </button>
        )}
      </div>

      {/* 2. Controls Row (Month Switcher & Filter Pills) */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 pt-1">
        {/* Month Switcher */}
        <div className="flex items-center gap-1.5 border border-gray-200 rounded-xl px-2 py-1 bg-white w-fit shadow-2xs">
          <button
            type="button"
            onClick={onPrevMonth}
            className="w-7 h-7 rounded-lg hover:bg-gray-100 active:scale-95 text-gray-600 flex items-center justify-center transition-all cursor-pointer"
            aria-label="Bulan sebelumnya"
          >
            <Icon icon="ph:caret-left-bold" className="w-3.5 h-3.5" />
          </button>

          <span className="text-xs sm:text-sm font-bold text-[#0E1726] px-2 select-none">
            {monthLabel}
          </span>

          <button
            type="button"
            onClick={onNextMonth}
            className="w-7 h-7 rounded-lg hover:bg-gray-100 active:scale-95 text-gray-600 flex items-center justify-center transition-all cursor-pointer"
            aria-label="Bulan berikutnya"
          >
            <Icon icon="ph:caret-right-bold" className="w-3.5 h-3.5" />
          </button>
        </div>

        {/* Counter & Segmented Filter Tabs */}
        <div className="flex items-center gap-3 self-end sm:self-auto flex-wrap">
          <span className="text-xs text-gray-400 font-normal">
            {totalCount} catatan · {manualCount} manual
          </span>

          <div className="inline-flex items-center bg-gray-100/80 p-0.5 rounded-xl border border-gray-200/60">
            {filters.map((f) => {
              const isActive = currentFilter === f.key;
              return (
                <button
                  key={f.key}
                  type="button"
                  onClick={() => onFilterChange(f.key)}
                  className={`px-3 py-1 rounded-lg text-xs font-semibold transition-all cursor-pointer ${
                    isActive
                      ? 'bg-white text-[#FC480D] shadow-2xs font-bold'
                      : 'text-gray-500 hover:text-gray-900 font-medium'
                  }`}
                >
                  {f.label}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* 3. DESKTOP VIEW: Data Table (>= md) */}
      <div className="hidden md:block overflow-x-auto -mx-5 px-5 pt-1">
        <table className="w-full text-left border-collapse">
          <thead>
            <tr className="border-b border-gray-100 text-[11px] font-bold text-gray-400 uppercase tracking-wider">
              <th className="py-3 px-4 w-32">TANGGAL</th>
              <th className="py-3 px-4">KETERANGAN</th>
              <th className="py-3 px-4 text-center w-28">KATEGORI</th>
              <th className="py-3 px-4 text-center w-28">PENCATATAN</th>
              <th className="py-3 px-4 text-right w-44">JUMLAH</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 text-xs">
            {isLoading ? (
              [1, 2, 3, 4, 5].map((i) => (
                <tr key={i} className="animate-pulse">
                  <td className="py-4 px-4"><div className="h-4 bg-gray-200 rounded w-20" /></td>
                  <td className="py-4 px-4">
                    <div className="space-y-1.5">
                      <div className="h-4 bg-gray-200 rounded w-44" />
                      <div className="h-3 bg-gray-100 rounded w-28" />
                    </div>
                  </td>
                  <td className="py-4 px-4 text-center"><div className="h-4 bg-gray-100 rounded-full w-14 mx-auto" /></td>
                  <td className="py-4 px-4 text-center"><div className="h-4 bg-gray-100 rounded-full w-16 mx-auto" /></td>
                  <td className="py-4 px-4 text-right"><div className="h-4 bg-gray-200 rounded w-20 ml-auto" /></td>
                </tr>
              ))
            ) : completedTransactions.length === 0 ? (
              <tr>
                <td colSpan={5} className="py-12 text-center text-gray-400">
                  <div className="w-10 h-10 rounded-2xl bg-orange-50 text-[#FC480D] flex items-center justify-center mx-auto mb-2 border border-orange-100/60">
                    <Icon icon="ph:receipt-bold" className="w-5 h-5" />
                  </div>
                  <p className="font-bold text-gray-700 text-sm">Belum ada catatan kas</p>
                  <p className="text-xs text-gray-400 mt-0.5">Tidak ada transaksi di bulan ini.</p>
                </td>
              </tr>
            ) : (
              completedTransactions.map((tx) => {
                const isIncome = tx.type === 'income';
                const isPending = tx.status === 'pending';
                return (
                  <tr key={tx.id} className="hover:bg-gray-50/70 transition-colors">
                    <td className="py-3.5 px-4 font-semibold text-gray-700 whitespace-nowrap">
                      {formatDateIndo(tx.transaction_date)}
                    </td>
                    <td className="py-3.5 px-4">
                      <div className="font-bold text-gray-900 text-sm leading-snug">{tx.title}</div>
                      {tx.description && (
                        <div className="text-gray-400 text-xs mt-0.5">{tx.description}</div>
                      )}
                    </td>
                    <td className="py-3.5 px-4 text-center">
                      {tx.category_name ? (
                        <span className="inline-block text-[11px] font-medium text-gray-600 bg-gray-100/80 border border-gray-200/50 px-2.5 py-0.5 rounded-md">
                          {tx.category_name}
                        </span>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </td>
                    <td className="py-3.5 px-4 text-center">
                      {!tx.is_manual ? (
                        <span className="inline-block text-[11px] font-semibold text-blue-600 bg-blue-50 px-2.5 py-0.5 rounded-full">
                          Otomatis
                        </span>
                      ) : (
                        <span className="inline-block text-[11px] font-semibold text-purple-600 bg-purple-50 px-2.5 py-0.5 rounded-full">
                          Manual
                        </span>
                      )}
                    </td>
                    <td className="py-3.5 px-4 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <span
                          className={`font-bold text-sm tracking-tight ${
                            isPending
                              ? 'text-[#D97706]'
                              : isIncome
                              ? 'text-[#059669]'
                              : 'text-[#EF4444]'
                          }`}
                        >
                          {isPending
                            ? `+ ${formatRupiahPlain(tx.amount)}`
                            : isIncome
                            ? `+ ${formatRupiahPlain(tx.amount)}`
                            : `- ${formatRupiahPlain(tx.amount)}`}
                        </span>

                        {!tx.is_manual ? (
                          <span className="text-gray-300" title="Tercatat otomatis">
                            <Icon icon="ph:lock-simple" className="w-3.5 h-3.5" />
                          </span>
                        ) : (
                          <button
                            type="button"
                            onClick={(e) => handleOpenMenu(e, tx)}
                            className="w-7 h-7 rounded-full hover:bg-gray-100 flex items-center justify-center text-gray-400 hover:text-gray-600 transition-colors cursor-pointer"
                            aria-label="Aksi"
                          >
                            <Icon icon="ph:dots-three-vertical-bold" className="w-3.5 h-3.5" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* 4. MOBILE VIEW: Compact List (< md) */}
      <div className="md:hidden">
        <div className="border-t border-[#E0E6ED] -mx-4 sm:-mx-5 my-2" />

        {isLoading ? (
          <div className="divide-y divide-gray-100 animate-pulse">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="py-3.5 first:pt-1 last:pb-1 flex items-start justify-between gap-3">
                <div className="space-y-2 flex-1">
                  <div className="h-4 bg-gray-200 rounded w-48" />
                  <div className="h-3 bg-gray-100 rounded w-24" />
                  <div className="flex items-center gap-1.5 pt-0.5">
                    <div className="h-4 bg-gray-100 rounded-full w-14" />
                    <div className="h-4 bg-gray-100 rounded-full w-16" />
                  </div>
                </div>
                <div className="h-4 bg-gray-200 rounded w-24 shrink-0" />
              </div>
            ))}
          </div>
        ) : completedTransactions.length === 0 ? (
          <div className="text-center py-10 px-4">
            <div className="w-12 h-12 rounded-2xl bg-orange-50 text-[#FC480D] flex items-center justify-center mx-auto mb-3 border border-orange-100/60">
              <Icon icon="ph:receipt-bold" className="w-6 h-6" />
            </div>
            <h3 className="text-sm font-bold text-[#0E1726]">Belum ada catatan kas</h3>
            <p className="text-xs text-gray-400 mt-1 max-w-xs mx-auto leading-relaxed">
              Transaksi event dan pencatatan kas di bulan ini akan otomatis tercatat di sini.
            </p>
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {completedTransactions.map((tx) => {
              const isIncome = tx.type === 'income';
              const isPending = tx.status === 'pending';

              return (
                <div key={tx.id} className="py-3.5 first:pt-1 last:pb-1">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <h3 className="text-sm font-bold text-[#0E1726] leading-snug line-clamp-1">
                        {tx.title}
                      </h3>
                      <div className="text-xs text-gray-400 font-normal mt-0.5">
                        {formatDateIndo(tx.transaction_date)}
                      </div>

                      <div className="flex items-center gap-1.5 mt-2 flex-wrap">
                        {tx.category_name && (
                          <span className="text-[11px] font-medium text-gray-600 bg-gray-100 px-2.5 py-0.5 rounded-full">
                            {tx.category_name}
                          </span>
                        )}
                        {tx.is_manual ? (
                          <span className="text-[11px] font-medium text-purple-600 bg-purple-50 px-2.5 py-0.5 rounded-full">
                            Manual
                          </span>
                        ) : (
                          <span className="text-[11px] font-medium text-blue-600 bg-blue-50 px-2.5 py-0.5 rounded-full">
                            Otomatis
                          </span>
                        )}
                      </div>
                    </div>

                    <div className="flex items-center gap-1.5 shrink-0 pt-0.5">
                      <span
                        className={`text-sm sm:text-base font-bold tracking-tight ${
                          isPending
                            ? 'text-[#D97706]'
                            : isIncome
                            ? 'text-[#059669]'
                            : 'text-[#EF4444]'
                        }`}
                      >
                        {isPending
                          ? `+ ${formatRupiahPlain(tx.amount)}`
                          : isIncome
                          ? `+ ${formatRupiahPlain(tx.amount)}`
                          : `- ${formatRupiahPlain(tx.amount)}`}
                      </span>

                      {!tx.is_manual ? (
                        <span className="text-gray-300" title="Tercatat otomatis dari event">
                          <Icon icon="ph:lock-simple" className="w-4 h-4" />
                        </span>
                      ) : (
                        <button
                          type="button"
                          onClick={(e) => handleOpenMenu(e, tx)}
                          className="w-7 h-7 rounded-full hover:bg-gray-100 flex items-center justify-center text-gray-400 hover:text-gray-600 transition-colors cursor-pointer"
                          aria-label="Aksi"
                        >
                          <Icon icon="ph:dots-three-vertical-bold" className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* 5. Pagination Controls (Per 10 Data) */}
      {totalCount > 0 && (
        <div className="flex flex-col sm:flex-row items-center justify-between gap-3 pt-3.5 border-t border-[#E0E6ED] text-xs text-gray-500">
          <div>
            Menampilkan <span className="font-semibold text-gray-700">{totalCount === 0 ? 0 : (page - 1) * 10 + 1}</span>–
            <span className="font-semibold text-gray-700">{Math.min(page * 10, totalCount)}</span> dari{' '}
            <span className="font-semibold text-gray-700">{totalCount}</span> catatan kas
          </div>

          {totalPages > 1 && (
            <div className="flex items-center gap-1.5">
              <button
                type="button"
                disabled={page <= 1}
                onClick={() => onPageChange(page - 1)}
                className="px-2.5 py-1.5 rounded-lg border border-gray-200 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors flex items-center gap-1 text-gray-700 font-medium cursor-pointer"
              >
                <Icon icon="ph:caret-left-bold" className="w-3.5 h-3.5" />
                <span className="hidden sm:inline">Sebelumnya</span>
              </button>

              <div className="flex items-center gap-1">
                {Array.from({ length: totalPages }, (_, i) => i + 1)
                  .filter((p) => p === 1 || p === totalPages || Math.abs(p - page) <= 1)
                  .map((p, idx, arr) => (
                    <React.Fragment key={p}>
                      {idx > 0 && arr[idx - 1] !== p - 1 && (
                        <span className="px-1 text-gray-400">...</span>
                      )}
                      <button
                        type="button"
                        onClick={() => onPageChange(p)}
                        className={`w-7 h-7 rounded-lg text-xs font-semibold flex items-center justify-center transition-colors cursor-pointer ${
                          page === p
                            ? 'bg-[#FC480D] text-white shadow-2xs'
                            : 'border border-gray-200 text-gray-700 hover:bg-gray-50'
                        }`}
                      >
                        {p}
                      </button>
                    </React.Fragment>
                  ))}
              </div>

              <button
                type="button"
                disabled={page >= totalPages}
                onClick={() => onPageChange(page + 1)}
                className="px-2.5 py-1.5 rounded-lg border border-gray-200 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors flex items-center gap-1 text-gray-700 font-medium cursor-pointer"
              >
                <span className="hidden sm:inline">Berikutnya</span>
                <Icon icon="ph:caret-right-bold" className="w-3.5 h-3.5" />
              </button>
            </div>
          )}
        </div>
      )}

      {/* 6. Floating Action Menu (Portalled to document.body to avoid table clipping & height expansion) */}
      {activeMenu &&
        createPortal(
          <>
            <div
              className="fixed inset-0 z-[60] bg-transparent"
              onClick={() => setActiveMenu(null)}
            />
            <div
              style={{
                position: 'fixed',
                top: activeMenu.top,
                left: activeMenu.left,
                width: 120,
              }}
              className="bg-white rounded-xl shadow-xl border border-[#E0E6ED] py-1 z-[61] divide-y divide-gray-50 text-left animate-in fade-in zoom-in-95 duration-75 select-none"
            >
              <button
                type="button"
                onClick={() => {
                  const tx = activeMenu.tx;
                  setActiveMenu(null);
                  onEditTransaction(tx);
                }}
                className="w-full px-3 py-2 text-xs font-semibold text-gray-700 hover:bg-gray-50 flex items-center gap-2 cursor-pointer transition-colors"
              >
                <Icon icon="ph:pencil-simple-bold" className="w-3.5 h-3.5 text-gray-500" />
                <span>Edit</span>
              </button>
              <button
                type="button"
                onClick={() => {
                  const tx = activeMenu.tx;
                  setActiveMenu(null);
                  onDeleteTransaction(tx);
                }}
                className="w-full px-3 py-2 text-xs font-semibold text-[#EF4444] hover:bg-red-50 flex items-center gap-2 cursor-pointer transition-colors"
              >
                <Icon icon="ph:trash-bold" className="w-3.5 h-3.5 text-[#EF4444]" />
                <span>Hapus</span>
              </button>
            </div>
          </>,
          document.body
        )}
    </div>
  );
};
